import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, RotateCcw, Home, Trophy, Clock, Volume2, VolumeX, Sparkles, Star } from 'lucide-react';
import CanetaPersonagem from '../components/CanetaPersonagem';
import MagicalParticles from '../components/MagicalParticles';
import { quizService } from '../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';
import { soundManager } from '../utils/sounds';

const Quiz = () => {
  const location = useLocation();
  const [etapa, setEtapa] = useState('inicio'); // inicio, introducao, nome, quiz, resultado
  const [nomeJogador, setNomeJogador] = useState('');
  const [emailJogador, setEmailJogador] = useState('');
  const [perguntas, setPerguntas] = useState([]);
  const [perguntaAtual, setPerguntaAtual] = useState(0);
  const [respostas, setRespostas] = useState([]);
  const [pontuacao, setPontuacao] = useState(0);
  const [respostaSelecionada, setRespostaSelecionada] = useState(null);
  const [mostrarFeedback, setMostrarFeedback] = useState(false);
  const [respostaCorreta, setRespostaCorreta] = useState(null);
  const [tempoInicio, setTempoInicio] = useState(null);
  const [posicaoRanking, setPosicaoRanking] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [particleColor, setParticleColor] = useState('golden');
  const [loading, setLoading] = useState(false);
  const [quizFinalizado, setQuizFinalizado] = useState(false);

  // Narrativas da Jornada pela Caneta Dourada
  const narrativas = {
    introducao: {
      titulo: "A Jornada pela Caneta Dourada",
      texto: [
        "Bem-vindo(a) à lendária Jornada pela Caneta Dourada! Uma aventura épica que testa seus conhecimentos sobre a história e tradições do ICSA.",
        "Há muito tempo, nas terras acadêmicas da Unifesspa, existe uma lenda sobre uma caneta mágica dourada que concede sabedoria infinita a quem conseguir encontrá-la.",
        "Para conquistar este tesouro, você deve enfrentar 10 desafios que testarão seu conhecimento sobre as Semanas Acadêmicas, professores, projetos e a rica história do nosso instituto.",
        "Cada resposta correta o aproxima da caneta dourada. Cada erro... bem, todos cometem erros na jornada do conhecimento!",
        "Está pronto para iniciar esta aventura e se tornar uma lenda do ICSA?"
      ]
    },
    pergunta: [
      "Você se depara com um antigo pergaminho...",
      "Um sábio professor aparece em seu caminho e questiona...",
      "A caneta dourada brilha mais forte. Ela sussurra uma pergunta...",
      "Nas profundezas da biblioteca, você encontra um livro que pergunta...",
      "O espírito de um ex-aluno surge e desafia você...",
      "A história do ICSA ecoa pelos corredores. Você ouve...",
      "Um portal do tempo se abre, revelando uma questão do passado...",
      "A caneta dourada pulsa com energia. Ela quer saber...",
      "Você chega a uma encruzilhada do conhecimento...",
      "O último guardião da caneta dourada o confronta..."
    ],
    acerto: [
      "Excelente! A caneta dourada brilha mais intensamente!",
      "Correto! Você está no caminho certo para a lenda!",
      "Perfeito! Sua sabedoria impressiona os espíritos do ICSA!",
      "Muito bem! A caneta dourada se aproxima de você!",
      "Incrível! Você demonstra verdadeiro conhecimento!"
    ],
    erro: [
      "Ops! Todo aventureiro comete erros. Continue sua jornada!",
      "Não foi dessa vez, mas a persistência é a chave do sucesso!",
      "A caneta dourada ainda está distante, mas não desista!",
      "Erro faz parte da jornada do conhecimento. Siga em frente!",
      "A sabedoria vem com a experiência. Continue tentando!"
    ]
  };

  useEffect(() => {
    carregarPerguntas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarPerguntas = async () => {
    try {
      setLoading(true);
      const { data, error } = await quizService.getQuestions();
      if (error) throw new Error(error);
      
      // Selecionar 10 questões aleatoriamente
      const todasPerguntas = data || [];
      const perguntasAleatorias = selecionarPerguntasAleatorias(todasPerguntas, 10);
      setPerguntas(perguntasAleatorias);
    } catch (error) {
      toast.error('Erro ao carregar perguntas do quiz');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para selecionar perguntas aleatoriamente sem repetições usando Fisher-Yates shuffle
  const selecionarPerguntasAleatorias = (perguntas, quantidade) => {
    if (perguntas.length <= quantidade) {
      return perguntas; // Se há menos perguntas que o solicitado, retorna todas
    }
    
    // Cria uma cópia do array para não modificar o original
    const perguntasCopia = [...perguntas];
    
    // Fisher-Yates shuffle para embaralhar aleatoriamente
    for (let i = perguntasCopia.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [perguntasCopia[i], perguntasCopia[j]] = [perguntasCopia[j], perguntasCopia[i]];
    }
    
    return perguntasCopia.slice(0, quantidade);
  };

  const iniciarJornada = () => {
    soundManager.playStart();
    setEtapa('introducao');
  };

  const toggleSound = () => {
    const newState = soundManager.toggle();
    setSoundEnabled(newState);
    if (newState) {
      soundManager.playClick();
    }
  };

  const iniciarQuiz = () => {
    if (nomeJogador.trim() && perguntas.length > 0) {
      soundManager.playStart();
      // Iniciar música de fundo
      soundManager.playBackgroundMusic('/music/quiz.mp3');
      setEtapa('quiz');
      setPerguntaAtual(0);
      setRespostas([]);
      setPontuacao(0);
      setRespostaSelecionada(null);
      setMostrarFeedback(false);
      setTempoInicio(Date.now());
      setPosicaoRanking(null);
      setParticleColor('rainbow');
    } else if (perguntas.length === 0) {
      toast.error('Nenhuma pergunta disponível no momento');
    }
  };

  const obterNarrativaPergunta = () => {
    return narrativas.pergunta[perguntaAtual] || narrativas.pergunta[0];
  };

  const obterNarrativaFeedback = (acertou) => {
    const lista = acertou ? narrativas.acerto : narrativas.erro;
    return lista[Math.floor(Math.random() * lista.length)];
  };

  const selecionarResposta = (opcaoSelecionada) => {
    if (mostrarFeedback) return;
    
    soundManager.playClick();
    setRespostaSelecionada(opcaoSelecionada);
    const pergunta = perguntas[perguntaAtual];
    const estaCorreta = opcaoSelecionada === pergunta.correct_answer;
    
    setRespostaCorreta(pergunta.correct_answer);
    setMostrarFeedback(true);
    
    // Tocar som baseado na resposta
    setTimeout(() => {
      if (estaCorreta) {
        soundManager.playCorrect();
        setParticleColor('golden');
      } else {
        soundManager.playWrong();
        setParticleColor('purple');
      }
    }, 300);
    
    const novaResposta = {
      questionId: pergunta.id,
      answer: opcaoSelecionada,
      isCorrect: estaCorreta
    };
    
    setRespostas([...respostas, novaResposta]);
    
    if (estaCorreta) {
      setPontuacao(pontuacao + pergunta.points);
    }
  };

  const proximaPergunta = async () => {
    if (perguntaAtual < perguntas.length - 1) {
      soundManager.playProgress();
      setParticleColor('rainbow');
      setPerguntaAtual(perguntaAtual + 1);
      setRespostaSelecionada(null);
      setMostrarFeedback(false);
      setRespostaCorreta(null);
    } else {
      // Finalizar quiz e submeter ao Supabase
      if (quizFinalizado || loading) return; // Prevenir múltiplas submissões
      
      try {
        setLoading(true);
        setQuizFinalizado(true);
        
        // Incluir a última resposta antes de submeter
        const respostasCompletas = [...respostas];
        if (respostaSelecionada) {
          const pergunta = perguntas[perguntaAtual];
          const estaCorreta = respostaSelecionada === pergunta.correct_answer;
          respostasCompletas.push({
            questionId: pergunta.id,
            answer: respostaSelecionada,
            isCorrect: estaCorreta
          });
        }
        
        // Calcular tempo gasto em segundos
        const timeSpentSeconds = Math.floor((Date.now() - tempoInicio) / 1000);
        
        const { error } = await quizService.submitQuiz(
          nomeJogador,
          emailJogador,
          respostasCompletas,
          timeSpentSeconds
        );
        
        if (error) throw new Error(error);
        
        // Obter ranking atualizado
        const { data: ranking } = await quizService.getRanking();
        const posicao = ranking?.findIndex(p => p.name === nomeJogador) + 1 || null;
        
        setPosicaoRanking(posicao);
        soundManager.playVictory();
        // Parar música de fundo
        soundManager.stopBackgroundMusic();
        setParticleColor('rainbow');
        setEtapa('resultado');
        toast.success('Quiz finalizado com sucesso!');
      } catch (error) {
        toast.error('Erro ao finalizar quiz');
        console.error('Erro:', error);
        setQuizFinalizado(false); // Permitir nova tentativa em caso de erro
      } finally {
        setLoading(false);
      }
    }
  };

  const reiniciarQuiz = () => {
    soundManager.playClick();
    // Parar música de fundo se estiver tocando
    soundManager.stopBackgroundMusic();
    setEtapa('inicio');
    setNomeJogador('');
    setEmailJogador('');
    setPerguntaAtual(0);
    setRespostas([]);
    setPontuacao(0);
    setRespostaSelecionada(null);
    setMostrarFeedback(false);
    setRespostaCorreta(null);
    setTempoInicio(null);
    setPosicaoRanking(null);
    setQuizFinalizado(false);
    setParticleColor('golden');
    carregarPerguntas(); // Carregar novas perguntas aleatórias
  };

  const obterTitulo = (pontos) => {
    const totalPontos = perguntas.reduce((acc, p) => acc + p.points, 0);
    const percentual = (pontos / totalPontos) * 100;
    
    if (percentual >= 90) return { titulo: 'Guardião do Conhecimento', mensagem: 'Parabéns! Você domina completamente o conteúdo!' };
    if (percentual >= 70) return { titulo: 'Historiador Acadêmico', mensagem: 'Excelente! Você tem um ótimo conhecimento!' };
    if (percentual >= 50) return { titulo: 'Explorador do Saber', mensagem: 'Muito bem! Continue explorando o conhecimento!' };
    return { titulo: 'Calouro Curioso', mensagem: 'Bom começo! Continue estudando e aprendendo!' };
  };

  const obterProgresso = () => {
    return perguntas.length > 0 ? ((perguntaAtual + 1) / perguntas.length) * 100 : 0;
  };

  const formatarTempo = (segundos) => {
    const minutos = Math.floor(segundos / 60);
    const segsRestantes = segundos % 60;
    return `${minutos}:${segsRestantes < 10 ? '0' : ''}${segsRestantes}`;
  };

  // Loading state
  if (loading && perguntas.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Toaster position="top-right" />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando perguntas...</p>
        </div>
      </div>
    );
  }

  // Tela de Início
  if (etapa === 'inicio') {
    return (
      <div className="min-h-screen flex items-center justify-center py-8 relative overflow-hidden bg-gradient-to-br from-amazonia-green-50 via-golden-50 to-purple-50">
        <MagicalParticles color={particleColor} intensity="medium" />
        <Toaster position="top-right" />
        
        {/* Botão de Som */}
        <button
          onClick={toggleSound}
          className="fixed top-4 right-4 z-50 bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110"
          title={soundEnabled ? 'Desativar sons' : 'Ativar sons'}
        >
          {soundEnabled ? (
            <Volume2 className="w-6 h-6 text-amazonia-green-600" />
          ) : (
            <VolumeX className="w-6 h-6 text-gray-400" />
          )}
        </button>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="mb-8">
              <div className="mb-6 animate-float">
                <CanetaPersonagem estado="celebrando" tamanho="lg" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amazonia-green-600 via-golden-600 to-purple-600 mb-4 animate-pulse">
                ✨ Quiz da 8ª Semana Acadêmica ✨
              </h1>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white bg-opacity-90 backdrop-blur-sm rounded-xl shadow-2xl p-8 mb-8 max-w-3xl mx-auto border-2 border-golden-200"
            >
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Sparkles className="w-6 h-6 text-golden-600 animate-spin" style={{ animationDuration: '3s' }} />
                <Star className="w-5 h-5 text-purple-600 animate-bounce" />
                <Sparkles className="w-6 h-6 text-amazonia-green-600 animate-spin" style={{ animationDuration: '3s' }} />
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Teste seus conhecimentos sobre a 8ª Semana Acadêmica do ICSA! 
                Responda às perguntas e descubra seu nível de conhecimento sobre o evento.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-amazonia-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-amazonia-green-600">{perguntas.length}</div>
                  <div className="text-sm text-gray-600">Perguntas</div>
                </div>
                <div className="text-center p-4 bg-golden-50 rounded-lg">
                  <div className="text-2xl font-bold text-golden-600">4</div>
                  <div className="text-sm text-gray-600">Títulos</div>
                </div>
                <div className="text-center p-4 bg-amazonia-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-amazonia-green-600">🏆</div>
                  <div className="text-sm text-gray-600">Ranking</div>
                </div>
                <div className="text-center p-4 bg-golden-50 rounded-lg">
                  <div className="text-2xl font-bold text-golden-600">∞</div>
                  <div className="text-sm text-gray-600">Diversão</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-lg p-6 max-w-md mx-auto"
            >
              <h3 className="text-xl font-semibold text-amazonia-dark-800 mb-4">
                Pronto para começar?
              </h3>
              <button
                onClick={iniciarJornada}
                disabled={loading}
                className="w-full bg-gradient-to-r from-golden-500 to-golden-600 hover:from-golden-600 hover:to-golden-700 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-lg flex items-center justify-center space-x-2 animate-glow hover:shadow-2xl"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Trophy className="w-5 h-5" />
                    <span>Iniciar Jornada pela Caneta Dourada</span>
                  </>
                )}
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Tela de Introdução da Jornada
  if (etapa === 'introducao') {
    return (
      <div className="min-h-screen flex items-center justify-center py-8 relative overflow-hidden bg-gradient-to-br from-golden-100 via-amazonia-green-50 to-purple-100">
        <MagicalParticles color={particleColor} intensity="high" />
        <Toaster position="top-right" />
        
        {/* Botão de Som */}
        <button
          onClick={toggleSound}
          className="fixed top-4 right-4 z-50 bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110"
          title={soundEnabled ? 'Desativar sons' : 'Ativar sons'}
        >
          {soundEnabled ? (
            <Volume2 className="w-6 h-6 text-amazonia-green-600" />
          ) : (
            <VolumeX className="w-6 h-6 text-gray-400" />
          )}
        </button>
        
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-golden-50 to-amazonia-green-50 rounded-2xl shadow-2xl p-8 text-center border-4 border-golden-200"
          >
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-6 animate-float"
            >
              <CanetaPersonagem mood="excited" size="large" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-golden-600 to-amazonia-green-600 mb-8"
            >
              {narrativas.introducao.titulo}
            </motion.h1>

            <div className="space-y-4 mb-8 text-left max-w-3xl mx-auto">
              {narrativas.introducao.texto.map((paragrafo, index) => (
                <motion.p
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="text-lg text-gray-700 leading-relaxed p-4 bg-white bg-opacity-70 rounded-lg border-l-4 border-golden-400"
                >
                  {paragrafo}
                </motion.p>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white rounded-xl shadow-lg p-6 max-w-md mx-auto mb-6"
            >
              <h3 className="text-xl font-semibold text-amazonia-dark-800 mb-4">
                Identifique-se, aventureiro(a)!
              </h3>
              <input
                type="text"
                value={nomeJogador}
                onChange={(e) => setNomeJogador(e.target.value)}
                placeholder="Digite seu nome..."
                className="w-full px-4 py-3 border-2 border-amazonia-green-200 rounded-lg focus:border-amazonia-green-500 focus:outline-none text-lg mb-4"
              />
              <input
                type="email"
                value={emailJogador}
                onChange={(e) => setEmailJogador(e.target.value)}
                placeholder="Digite seu email (opcional)..."
                className="w-full px-4 py-3 border-2 border-amazonia-green-200 rounded-lg focus:border-amazonia-green-500 focus:outline-none text-lg mb-4"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <button
                onClick={() => setEtapa('inicio')}
                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <Home className="w-5 h-5" />
                <span>Voltar</span>
              </button>
              <button
                onClick={iniciarQuiz}
                disabled={!nomeJogador.trim() || loading}
                className="bg-gradient-to-r from-golden-500 to-golden-600 hover:from-golden-600 hover:to-golden-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2 animate-glow hover:shadow-2xl"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    <span>Iniciar Jornada!</span>
                  </>
                )}
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Tela do Quiz
  if (etapa === 'quiz') {
    if (perguntas.length === 0) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Toaster position="top-right" />
          <div className="text-center">
            <p className="text-gray-600">Nenhuma pergunta disponível</p>
          </div>
        </div>
      );
    }

    const pergunta = perguntas[perguntaAtual];
    
    return (
      <div className="min-h-screen py-8 relative overflow-hidden bg-gradient-to-br from-purple-50 via-amazonia-green-50 to-golden-50">
        <MagicalParticles color={particleColor} intensity="high" />
        <Toaster position="top-right" />
        
        {/* Botão de Som */}
        <button
          onClick={toggleSound}
          className="fixed top-4 right-4 z-50 bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110"
          title={soundEnabled ? 'Desativar sons' : 'Ativar sons'}
        >
          {soundEnabled ? (
            <Volume2 className="w-6 h-6 text-amazonia-green-600" />
          ) : (
            <VolumeX className="w-6 h-6 text-gray-400" />
          )}
        </button>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header com progresso */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-xl shadow-2xl p-6 border-2 border-purple-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <CanetaPersonagem 
                    estado={mostrarFeedback ? (respostaSelecionada === respostaCorreta ? 'feliz' : 'triste') : 'pensando'} 
                    tamanho="md" 
                  />
                  <div>
                    <h2 className="text-2xl font-bold text-amazonia-dark-800">
                      Olá, {nomeJogador}!
                    </h2>
                    <p className="text-gray-600">
                      Pergunta {perguntaAtual + 1} de {perguntas.length}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-golden-600 flex items-center space-x-2">
                    <Trophy className="w-6 h-6" />
                    <span>{pontuacao}</span>
                  </div>
                  <p className="text-sm text-gray-600">Pontos</p>
                </div>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-3">
                <motion.div
                  className="bg-gradient-to-r from-amazonia-green-500 to-golden-500 h-3 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${obterProgresso()}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          </motion.div>

          {/* Narrativa da Pergunta */}
          <motion.div
            key={`narrativa-${perguntaAtual}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-golden-50 to-amazonia-green-50 rounded-xl shadow-lg p-6 mb-4 border-l-4 border-golden-400"
          >
            <div className="flex items-center space-x-3">
              <CanetaPersonagem mood="thinking" size="small" />
              <p className="text-lg text-gray-700 italic font-medium">
                {obterNarrativaPergunta()}
              </p>
            </div>
          </motion.div>

          {/* Pergunta */}
          <motion.div
            key={`pergunta-${perguntaAtual}`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white bg-opacity-95 backdrop-blur-sm rounded-xl shadow-2xl p-8 mb-6 border-2 border-golden-200"
          >
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Star className="w-5 h-5 text-golden-600 animate-pulse" />
              <Sparkles className="w-4 h-4 text-purple-600" />
            </div>
            <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amazonia-green-700 to-purple-700 mb-6 text-center">
              {pergunta.question}
            </h3>
            
            <div className="space-y-4">
              {[
                { letra: 'A', texto: pergunta.option_a },
                { letra: 'B', texto: pergunta.option_b },
                { letra: 'C', texto: pergunta.option_c },
                { letra: 'D', texto: pergunta.option_d }
              ].map((opcao) => (
                <motion.button
                  key={opcao.letra}
                  onClick={() => selecionarResposta(opcao.letra)}
                  className={`quiz-option ${
                    mostrarFeedback
                      ? opcao.letra === respostaCorreta
                        ? 'correct'
                        : opcao.letra === respostaSelecionada && opcao.letra !== respostaCorreta
                        ? 'incorrect'
                        : 'disabled'
                      : respostaSelecionada === opcao.letra
                      ? 'selected'
                      : ''
                  } w-full p-4 rounded-lg border-2 transition-all duration-300 text-left flex items-center space-x-4 disabled:cursor-not-allowed`}
                  disabled={mostrarFeedback}
                  whileHover={!mostrarFeedback ? { scale: 1.02 } : {}}
                  whileTap={!mostrarFeedback ? { scale: 0.98 } : {}}
                >
                  <div className="flex items-center space-x-4 w-full">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border-2 ${
                      mostrarFeedback
                        ? opcao.letra === respostaCorreta
                          ? 'bg-green-500 text-white border-green-500'
                          : opcao.letra === respostaSelecionada && opcao.letra !== respostaCorreta
                          ? 'bg-red-500 text-white border-red-500'
                          : 'border-gray-300 text-gray-500'
                        : respostaSelecionada === opcao.letra
                        ? 'bg-amazonia-green-500 text-white border-amazonia-green-500'
                        : 'border-gray-300 text-gray-500'
                    }`}>
                      {opcao.letra}
                    </div>
                    <span className="flex-1 text-left">{opcao.texto}</span>
                    {mostrarFeedback && opcao.letra === respostaCorreta && (
                      <span className="text-green-600">✓</span>
                    )}
                    {mostrarFeedback && opcao.letra === respostaSelecionada && opcao.letra !== respostaCorreta && (
                      <span className="text-red-600">✗</span>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Feedback */}
          <AnimatePresence>
            {mostrarFeedback && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                className={`rounded-xl p-6 mb-6 ${
                  respostaSelecionada === respostaCorreta
                    ? 'bg-green-50 border-2 border-green-200 magical-glow'
                    : 'bg-red-50 border-2 border-red-200'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CanetaPersonagem 
                      mood={respostaSelecionada === respostaCorreta ? 'happy' : 'sad'} 
                      size="medium" 
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold mb-3 text-xl ${
                      respostaSelecionada === respostaCorreta
                        ? 'text-green-800'
                        : 'text-red-800'
                    }`}>
                      {obterNarrativaFeedback(respostaSelecionada === respostaCorreta)}
                    </h4>
                    {respostaSelecionada !== respostaCorreta && (
                      <p className="text-gray-700 mb-2">
                        <strong>Resposta correta:</strong> {respostaCorreta}
                      </p>
                    )}
                    {pergunta.explanation && (
                      <p className="text-gray-600 text-sm leading-relaxed">
                        <strong>Explicação:</strong> {pergunta.explanation}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="mt-6 text-center">
                  <button
                    onClick={proximaPergunta}
                    className="bg-gradient-to-r from-amazonia-green-500 to-amazonia-green-600 hover:from-amazonia-green-600 hover:to-amazonia-green-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center space-x-2 mx-auto"
                  >
                    <span>{perguntaAtual < perguntas.length - 1 ? 'Próxima Pergunta' : 'Ver Resultado'}</span>
                    <span>→</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // Tela de Resultado
  if (etapa === 'resultado') {
    const titulo = obterTitulo(pontuacao);
    
    return (
      <div className="min-h-screen flex items-center justify-center py-8 relative overflow-hidden bg-gradient-to-br from-golden-100 via-purple-100 to-amazonia-green-100">
        <MagicalParticles color="rainbow" intensity="high" />
        <Toaster position="top-right" />
        
        {/* Botão de Som */}
        <button
          onClick={toggleSound}
          className="fixed top-4 right-4 z-50 bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110"
          title={soundEnabled ? 'Desativar sons' : 'Ativar sons'}
        >
          {soundEnabled ? (
            <Volume2 className="w-6 h-6 text-amazonia-green-600" />
          ) : (
            <VolumeX className="w-6 h-6 text-gray-400" />
          )}
        </button>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            {/* Título conquistado */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-8"
            >
              <div className="mb-6 animate-float">
                <CanetaPersonagem 
                  estado={pontuacao >= 7 ? 'celebrando' : pontuacao >= 4 ? 'feliz' : 'normal'} 
                  tamanho="lg" 
                />
              </div>
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Star className="w-8 h-8 text-golden-600 animate-spin" style={{ animationDuration: '3s' }} />
                <Sparkles className="w-6 h-6 text-purple-600 animate-bounce" />
                <Trophy className="w-8 h-8 text-amazonia-green-600 animate-pulse" />
                <Sparkles className="w-6 h-6 text-golden-600 animate-bounce" />
                <Star className="w-8 h-8 text-purple-600 animate-spin" style={{ animationDuration: '3s' }} />
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-golden-600 via-purple-600 to-amazonia-green-600 mb-4">
                🎉 Parabéns, {nomeJogador}! 🎉
              </h1>
              <h2 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amazonia-green-600 to-golden-600 mb-2 animate-pulse">
                ✨ {titulo.titulo} ✨
              </h2>
              {posicaoRanking && (
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-2 rounded-full inline-flex items-center space-x-2 mb-4">
                  <Trophy className="w-5 h-5" />
                  <span className="font-semibold">#{posicaoRanking} no Ranking</span>
                </div>
              )}
            </motion.div>

            {/* Estatísticas */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white bg-opacity-90 backdrop-blur-sm rounded-xl shadow-2xl p-8 mb-8 border-2 border-golden-200"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-amazonia-green-50 rounded-lg">
                  <div className="text-3xl font-bold text-amazonia-green-600 mb-2">
                    {pontuacao}
                  </div>
                  <div className="text-gray-600 text-sm">Acertos</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-3xl font-bold text-red-600 mb-2">
                    {10 - pontuacao}
                  </div>
                  <div className="text-gray-600 text-sm">Erros</div>
                </div>
                <div className="text-center p-4 bg-golden-50 rounded-lg">
                  <div className="text-3xl font-bold text-golden-600 mb-2">
                    {Math.round((pontuacao / 10) * 100)}%
                  </div>
                  <div className="text-gray-600 text-sm">Aproveitamento</div>
                </div>
                {tempoInicio && (
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-3xl font-bold text-blue-600 mb-2 flex items-center justify-center">
                      <Clock className="w-6 h-6 mr-1" />
                      <span className="text-lg">{formatarTempo(Math.floor((Date.now() - tempoInicio) / 1000))}</span>
                    </div>
                    <div className="text-gray-600 text-sm">Tempo</div>
                  </div>
                )}
              </div>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                {titulo.mensagem}
              </p>
            </motion.div>

            {/* Ações */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <button
                onClick={reiniciarQuiz}
                className="bg-gradient-to-r from-amazonia-green-500 to-amazonia-green-600 hover:from-amazonia-green-600 hover:to-amazonia-green-700 text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg text-lg flex items-center justify-center space-x-2"
              >
                <RotateCcw className="w-5 h-5" />
                <span>Jogar Novamente</span>
              </button>
              <button
                onClick={() => window.location.href = '/ranking'}
                className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg text-lg flex items-center justify-center space-x-2"
              >
                <Trophy className="w-5 h-5" />
                <span>Ver Ranking</span>
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="bg-gradient-to-r from-golden-500 to-golden-600 hover:from-golden-600 hover:to-golden-700 text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg text-lg flex items-center justify-center space-x-2"
              >
                <Home className="w-5 h-5" />
                <span>Voltar ao Início</span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
};

export default Quiz;
